import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContentCard from '@/components/feature/ContentCard';
import useSEO from '@/hooks/useSEO';
import { getContentById, getRelated, typeLabels, typePaths } from '@/lib/content';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getContentById(id) : undefined;
  const [copied, setCopied] = useState(false);

  const seoTitle = item
    ? item.type === 'cast'
      ? `演员${item.title}详细资料 - 我的朋友他的妻子`
      : item.type === 'news'
        ? `${item.title} - 我的朋友他的妻子影视资讯`
        : `${item.title} 剧情详情 - 我的朋友他的妻子`
    : '内容不存在 - 我的朋友他的妻子';

  useSEO({
    title: seoTitle,
    description: item
      ? item.excerpt
      : '你访问的内容可能已经调整或不存在，请返回首页或使用站内搜索查找剧情、演员与资讯。',
    keywords: item ? [item.type === 'cast' ? '演员资料' : item.type === 'story' ? '剧情介绍' : '影视资讯', ...item.tags].join(',') : '内容不存在',
  });

  useEffect(() => {
    setCopied(false);
  }, [id]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '内容不存在' }]} />
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 px-4 py-20 text-center">
            <i className="ri-error-warning-line text-4xl text-primary-500/70"></i>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              内容不存在
            </h1>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-400">
              这条内容的编号没有被找到，可能是因为链接输入有误。你可以回到首页，或者搜索关键词重新找到它。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-primary-300"
              >
                <i className="ri-search-line"></i>
                站内搜索
              </Link>
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelated(item, 3);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2400);
    } catch {
      setCopied(false);
    }
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/50 px-4 py-8 md:px-8 md:py-12 lg:px-12">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: typeLabels[item.type], to: typePaths[item.type] },
            { label: '详情' },
          ]}
        />
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-900/60 px-3 py-1 text-[11px] text-accent-200">
                {typeLabels[item.type]}
              </span>
              <span className="text-[11px] text-foreground-500">{item.dateLabel}</span>
              {item.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-secondary-700/60 px-2.5 py-0.5 text-[11px] text-foreground-400"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-3 text-sm text-foreground-400 md:text-base">{item.subtitle}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-sm text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-primary-300 cursor-pointer"
            >
              <i className={copied ? 'ri-check-line text-primary-400' : 'ri-link'}></i>
              {copied ? '链接已复制' : '复制本页链接'}
            </button>
            <Link
              to={typePaths[item.type]}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-sm text-foreground-400 transition-colors hover:text-foreground-950"
            >
              <i className="ri-arrow-left-line"></i>
              返回{typeLabels[item.type]}
            </Link>
          </div>
        </div>
        {copied && (
          <p className="mt-3 text-xs text-primary-400 animate-fade-up">
            页面链接已复制到剪贴板，可直接粘贴分享。本站不提供其他分享渠道。
          </p>
        )}
      </div>

      <section className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
          <article className="min-w-0">
            <div
              className={`overflow-hidden rounded-lg border border-background-200/80 bg-background-100 ${
                item.type === 'cast' ? 'max-w-md' : 'w-full'
              }`}
            >
              <img
                src={item.image}
                alt={`${item.title} 主视觉`}
                title={`${item.title} - 我的朋友他的妻子`}
                className={`w-full object-cover object-top ${
                  item.type === 'cast' ? 'aspect-[4/5]' : 'aspect-[16/9]'
                }`}
              />
            </div>

            <p className="mt-8 border-l-2 border-primary-500 pl-4 text-base leading-relaxed text-foreground-700 md:text-lg">
              {item.excerpt}
            </p>

            <div className="mt-8 space-y-5 text-sm leading-loose text-foreground-700 md:text-base">
              {item.body.map((paragraph, index) => (
                <p key={`${item.id}-p-${index}`}>{paragraph}</p>
              ))}
            </div>

            <blockquote className="mt-10 rounded-lg border border-primary-500/25 bg-primary-950/30 p-5 md:p-6">
              <i className="ri-double-quotes-l text-xl text-primary-500/70"></i>
              <p className="mt-2 font-heading text-base leading-relaxed text-primary-200 md:text-lg">
                {item.quote}
              </p>
              <footer className="mt-3 text-xs text-foreground-500">—— 摘自{item.type === 'cast' ? '演员创作手记' : '剧集相关资料'}</footer>
            </blockquote>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-6">
              <span className="mr-1 text-xs text-foreground-600">相关标签：</span>
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300/70 px-3 py-1 text-xs text-foreground-400 transition-colors hover:border-primary-500/50 hover:text-primary-300"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </article>

          <aside className="space-y-5">
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-information-line text-primary-400"></i>
                内容信息
              </h2>
              <dl className="mt-4 space-y-3">
                {item.meta.map((field) => (
                  <div key={field.label} className="flex flex-col gap-1 border-b border-background-200/60 pb-3 last:border-0 last:pb-0">
                    <dt className="text-[11px] tracking-wide text-foreground-600">{field.label}</dt>
                    <dd className="text-sm leading-relaxed text-foreground-300">{field.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-compass-3-line text-primary-400"></i>
                继续浏览
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                <Link to="/story" className="flex items-center justify-between rounded-md border border-background-200/70 px-3 py-2.5 text-sm text-foreground-300 transition-colors hover:border-primary-500/40 hover:text-primary-300">
                  剧情介绍目录
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link to="/cast" className="flex items-center justify-between rounded-md border border-background-200/70 px-3 py-2.5 text-sm text-foreground-300 transition-colors hover:border-primary-500/40 hover:text-primary-300">
                  演员详情索引
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link to="/news" className="flex items-center justify-between rounded-md border border-background-200/70 px-3 py-2.5 text-sm text-foreground-300 transition-colors hover:border-primary-500/40 hover:text-primary-300">
                  影视资讯列表
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-secondary-700/40 bg-secondary-900 p-5">
              <p className="text-xs leading-relaxed text-foreground-500">
                本站为虚构剧集的内容展示站点，不含在线播放、下载与用户登录功能。站内资料仅用于演示内容组织方式。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-14 border-t border-background-200/70 pt-10">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <span className="h-px w-8 bg-primary-500"></span>
                  <span className="font-label text-xs tracking-[0.3em] text-primary-400">RELATED</span>
                </div>
                <h2 className="mt-2 font-heading text-lg font-bold text-foreground-950 md:text-2xl">
                  相关内容推荐
                </h2>
              </div>
              <Link
                to={typePaths[item.type]}
                className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-400 transition-colors hover:text-primary-400"
              >
                更多{typeLabels[item.type]}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((entry, index) => (
                <ContentCard key={entry.id} item={entry} variant="poster" index={index} />
              ))}
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}